import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Surface, TextField, Label, InputGroup, Button, Alert } from '@heroui/react'
import { useAuth } from '../../contexts/AuthContext'
import Lock from '~icons/solar/lock-password-bold'
import Eye from '~icons/solar/eye-bold'
import EyeOff from '~icons/solar/eye-closed-bold'
import Check from '~icons/solar/check-circle-bold'
import DangerCircle from '~icons/solar/danger-circle-bold'
import TrendingUp from '~icons/solar/graph-up-bold'
import LoadingSpinner from '~icons/svg-spinners/270-ring-with-bg'

type Step = 'set-password' | 'success' | 'invalid'

export default function AcceptInvitePage() {
  const navigate = useNavigate()
  const { login } = useAuth()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [step, setStep] = useState<Step>(token ? 'set-password' : 'invalid')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!password || !confirmPassword) {
      setError('Please fill in all fields')
      return
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)

    try {
      // Public endpoint -- no Supabase session exists yet, so this is a
      // plain fetch (not callAdminFunction-style), matching how other
      // verify_jwt=false functions in this project are called with no
      // auth header at all.
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/accept-affiliate-invite`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, password }),
        }
      )

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to set your password')
      }

      await login(data.email, password)
      setStep('success')

      setTimeout(() => {
        navigate('/affiliate/dashboard')
      }, 1500)
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-accent rounded-xl mb-4">
            <TrendingUp className="h-8 w-8 text-accent-foreground" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            {step === 'set-password' && 'Welcome aboard'}
            {step === 'success' && "You're all set!"}
            {step === 'invalid' && 'Invalid invite link'}
          </h1>
          <p className="text-muted">
            {step === 'set-password' && 'Set a password to activate your affiliate account'}
            {step === 'success' && 'Signing you in...'}
            {step === 'invalid' && 'This link is missing its invite token'}
          </p>
        </div>

        <Surface variant="default" className="rounded-2xl p-8">
          {step === 'set-password' && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <Alert status="danger" className="bg-danger-soft">
                  <Alert.Indicator />
                  <Alert.Content>
                    <Alert.Description className="text-danger-soft-foreground">{error}</Alert.Description>
                  </Alert.Content>
                </Alert>
              )}

              <TextField
                isRequired
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={password}
                onChange={setPassword}
              >
                <Label>Password</Label>
                <InputGroup variant="secondary">
                  <InputGroup.Prefix>
                    <Lock className="h-4 w-4" />
                  </InputGroup.Prefix>
                  <InputGroup.Input placeholder="Create a password" />
                  <InputGroup.Suffix>
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-muted hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </InputGroup.Suffix>
                </InputGroup>
                <p className="text-xs text-muted mt-1">At least 8 characters</p>
              </TextField>

              <TextField
                isRequired
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={confirmPassword}
                onChange={setConfirmPassword}
              >
                <Label>Confirm Password</Label>
                <InputGroup variant="secondary">
                  <InputGroup.Prefix>
                    <Lock className="h-4 w-4" />
                  </InputGroup.Prefix>
                  <InputGroup.Input placeholder="Confirm your password" />
                  <InputGroup.Suffix>
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="text-muted hover:text-foreground transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </InputGroup.Suffix>
                </InputGroup>
              </TextField>

              <Button type="submit" className="w-full" isDisabled={loading}>
                {loading ? (
                  <>
                    <LoadingSpinner className="h-4 w-4" />
                    Setting password...
                  </>
                ) : (
                  'Activate my account'
                )}
              </Button>
            </form>
          )}

          {step === 'success' && (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 bg-success-soft rounded-full flex items-center justify-center mx-auto">
                <Check className="h-8 w-8 text-success-soft-foreground" />
              </div>
              <p className="text-muted">Taking you to your dashboard...</p>
            </div>
          )}

          {step === 'invalid' && (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 bg-danger-soft rounded-full flex items-center justify-center mx-auto">
                <DangerCircle className="h-8 w-8 text-danger-soft-foreground" />
              </div>
              <p className="text-muted">
                This invite link doesn't have a token. Check the link in your email, or ask your
                program admin to resend your invite.
              </p>
              <Button variant="ghost" className="w-full" onPress={() => navigate('/auth')}>
                Back to Sign In
              </Button>
            </div>
          )}
        </Surface>
      </motion.div>
    </div>
  )
}
